import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import UnifiedModal from '../UnifiedModal';
import { useTranslation } from 'react-i18next';
import { getRentalTermsUrl } from '../../utils/rentalTerms';
import { BACKEND_API_ENDPOINT } from '../../constants/default';
import './RentalTermsModal.css';

// Sections already fetched per country, so reopening the modal doesn't refetch
const sectionCache = new Map();

const RentalTermsModal = ({ show, onHide, countryCode, dir }) => {
  const { t } = useTranslation();
  const [fetched, setFetched] = useState({ code: null, html: null, failed: false });

  useEffect(() => {
    if (!show || !countryCode || sectionCache.has(countryCode)) return undefined;

    const controller = new AbortController();
    setFetched({ code: countryCode, html: null, failed: false });
    fetch(`${BACKEND_API_ENDPOINT}/rentalTerms?provider=sixt&country=${countryCode}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((section) => {
        sectionCache.set(countryCode, section);
        setFetched({ code: countryCode, html: section, failed: false });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setFetched({ code: countryCode, html: null, failed: true });
      });

    return () => controller.abort();
  }, [show, countryCode]);

  if (!countryCode) return null;

  const current = fetched.code === countryCode ? fetched : null;
  const content = sectionCache.get(countryCode) ?? current?.html ?? null;
  const failed = !content && !!current?.failed;

  return (
    <UnifiedModal
      show={show}
      onHide={onHide}
      title={t('booking.rentalTermsTitle')}
      dir={dir}
      className="fullscreen-modal rental-terms-modal"
      showFooter={false}
      bodyStyle={{ padding: 0 }}
    >
      {content ? (
        <div dir="ltr" className="rental-terms-content text-left" dangerouslySetInnerHTML={{ __html: content }} />
      ) : failed ? (
        <div className="rental-terms-fallback">
          <a href={getRentalTermsUrl(countryCode)} target="_blank" rel="noopener noreferrer">
            {t('booking.fullRentalTerms')}
          </a>
        </div>
      ) : (
        <div className="rental-terms-loading d-flex align-items-center justify-content-center">
          <Spinner animation="border" role="status">
            <span className="sr-only">{t('loading')}</span>
          </Spinner>
        </div>
      )}
    </UnifiedModal>
  );
};

export default RentalTermsModal;
